import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { Button, DatePicker, Input, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import locale from 'antd/es/date-picker/locale/th_TH';
import { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { isFullAccess } from '@/utils/permissionUtils';
import { AxiosResponse } from 'axios';
import { searchCndns } from '@/adapters/cndn';
import { ISearchCndnReq, ISearchCndnRes, ICndn } from '@/interfaces/cndn';
import CndnTable from './components/cndn-table';
import { useAuth } from '@/contexts/AuthContext';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';

const { Search } = Input;

const _criteria: ISearchCndnReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const CndnNote = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISearchCndnReq>({
    ..._criteria,
    businessUnitId: userInfo?.bu || undefined,
  });

  const { data, refetch, isLoading, isFetching } = useQuery<
    AxiosResponse<ISearchCndnRes>,
    Error
  >({
    queryKey: ['search-cndns', criteria],
    queryFn: async () => await searchCndns(criteria),
  });

  const onActionOrder = () => {
    refetch();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICndn> | SorterResult<ICndn>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof ICndn) || newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: 'descend',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const perFullAccess = isFullAccess();

  return (
    <Card>
      <Title title="ใบเพิ่มหนี้/ ลดหนี้">
        <div className="flex justify-end gap-3">
          {perFullAccess && (
            <Button
              type="primary"
              onClick={() => navigate(`/cndn-note/create`)}
            >
              <PlusOutlined /> เพิ่มใหม่
            </Button>
          )}
        </div>
      </Title>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row justify-between gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <BusinessUnitSelector
              handleSelectData={function (id: number): void {
                setCriteria({ ...criteria, businessUnitId: id });
              }}
              disabled={!!userInfo?.bu}
              value={criteria?.businessUnitId}
            />
            <DatePicker.RangePicker
              size="large"
              locale={locale}
              onChange={onRangeChange}
            />
            <Search
              className="md:max-w-[400px] md:min-w-[400px]"
              placeholder="ค้นหา"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={onSearch}
            />
          </div>
        </div>
        <CndnTable
          criteria={criteria}
          cndn={data?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isFetching}
          onEditData={item => console.log(item)}
          onDelete={onActionOrder}
          onTableChange={onTableChange}
        />
      </div>
    </Card>
  );
};

export default CndnNote;
